import React from "react";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import moment from "moment";

import { enGB } from "date-fns/locale/en-GB";
registerLocale("en-GB", enGB);

import { useAppSelector } from "store/index";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import { getGeoDropdownOptions } from "helpers/geoDropdown";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import { ImageUpload } from "components/imageUpload";
import cn from "classnames";
import { capitalize } from "helpers/capitalize";
import { getDatePickerFormats, useTimeFormat } from "helpers/timeFormat";

interface PromoMainTabProps {
  payload: any;
  handleData: (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => void;
  clearFieldError: (field: string) => void;
  fieldErrors?: Record<string, string>;
  readOnly?: boolean;
}

const PromoMainTab: React.FC<PromoMainTabProps> = ({
  payload,
  handleData,
  clearFieldError,
  fieldErrors,
  readOnly,
}) => {
  const { t } = useTranslation();
  const datePickerFormats = getDatePickerFormats(useTimeFormat());
  const geoList = useAppSelector(selectFilteredGeo);

  return (
    <div className="pages-form-content mt-14">
      <div className="bonus-form-content">
        <div className="bonus-form-box">
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.title ?? ""}
              placeholder={t("enterTitle")}
              label={t("title")}
              type="text"
              id="promo-title"
              onChange={(e) => {
                handleData("title", e);
                clearFieldError("title");
              }}
              hasError={Boolean(fieldErrors?.["title"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.promoLabel ?? ""}
              placeholder={t("enterLabel")}
              label={capitalize(t("label"))}
              type="text"
              id="promo-label"
              onChange={(e) => {
                handleData("promoLabel", e);
                clearFieldError("promoLabel");
              }}
              hasError={Boolean(fieldErrors?.["promoLabel"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.slug ?? ""}
              placeholder={t("enterText")}
              label={t("slug")}
              type="text"
              id="promo-slug"
              onChange={(e) => {
                handleData("slug", e);
                clearFieldError("slug");
              }}
              hasError={Boolean(fieldErrors?.["slug"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.anchor ?? ""}
              placeholder={t("enterText")}
              label={t("anchor")}
              type="text"
              id="promo-anchor"
              onChange={(e) => {
                handleData("anchor", e);
                clearFieldError("anchor");
              }}
              hasError={Boolean(fieldErrors?.["anchor"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item bonus-form-box--row">
            <DefaultDropdown
              value={payload?.country}
              placeholder={t("geo")}
              label={t("geo")}
              options={getGeoDropdownOptions(geoList)}
              onSelect={(e) => {
                handleData("country", e);
                clearFieldError("country");
              }}
              hasError={Boolean(fieldErrors?.["country"])}
              disabled={readOnly}
            />
            <DefaultDropdown
              value={payload?.type ?? "all"}
              placeholder={t("promoType")}
              label={t("type")}
              options={[
                { label: t("all"), value: "all" },
                { label: t("casino"), value: "casino" },
                { label: t("sports"), value: "sports" },
              ]}
              onSelect={(e) => {
                handleData("type", e);
                clearFieldError("type");
              }}
              hasError={Boolean(fieldErrors?.["type"])}
              disabled={readOnly}
            />
          </div>
          <div className="bonus-form-box--item bonus-form-box--row">
            <div
              className={cn("date-picker-wrapper", {
                "has-error": fieldErrors?.["startedAt"],
              })}
            >
              <div className="title">{t("startedAt")}</div>
              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                showTimeSelect
                timeFormat={datePickerFormats.timeFormat}
                placeholderText={datePickerFormats.placeholder}
                dateFormat={datePickerFormats.dateFormat}
                selected={
                  payload?.startedAt
                    ? moment(
                        payload?.startedAt,
                        "DD.MM.YYYY HH:mm",
                        true,
                      ).toDate()
                    : null
                }
                onChange={(date) =>
                  handleData(
                    "startedAt",
                    moment(date).format("DD.MM.YYYY HH:mm"),
                  )
                }
                disabled={readOnly}
              />
            </div>
            <div
              className={cn("date-picker-wrapper", {
                "has-error": fieldErrors?.["finishedAt"],
              })}
            >
              <div className="title">{t("finishedAt")}</div>
              <DatePicker
                customInput={<ReadOnlyInput />}
                locale="en-GB"
                showTimeSelect
                timeFormat={datePickerFormats.timeFormat}
                placeholderText={datePickerFormats.placeholder}
                dateFormat={datePickerFormats.dateFormat}
                selected={
                  payload?.finishedAt
                    ? moment(
                        payload?.finishedAt,
                        "DD.MM.YYYY HH:mm",
                        true,
                      ).toDate()
                    : null
                }
                onChange={(date) =>
                  handleData(
                    "finishedAt",
                    moment(date).format("DD.MM.YYYY HH:mm"),
                  )
                }
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="bonus-form-switcher">
            <SwitchBtn
              activeTxt={t("active")}
              notActiveTxt={t("hidden")}
              isActive={!!payload?.isActive}
              handleClick={() =>
                handleData(
                  "isActive",
                  payload?.isActive ? !payload?.isActive : true,
                )
              }
              disabled={readOnly}
            />
            <br />
          </div>
        </div>
        <div className="bonus-form-box promo-images-box">
          <div className="game-form-img game-form-img--game center">
            <div className="label">{t("imageBig")}</div>

            <div
              className={cn("game-item", {
                "has-error": fieldErrors?.["file"],
              })}
            >
              <ImageUpload
                image={payload?.imageBig}
                isActive={payload?.isActive}
                onAddImage={(file) => {
                  handleData("imageBig", "", file);
                  clearFieldError("imageBig");
                }}
                disabled={readOnly}
              />
            </div>
          </div>
          <div className="game-form-img game-form-img--game center">
            <div className="label">{t("imageSmall")}</div>

            <div
              className={cn("game-item", {
                "has-error": fieldErrors?.["file"],
              })}
            >
              <ImageUpload
                image={payload?.imageSmall}
                isActive={payload?.isActive}
                onAddImage={(file) => {
                  handleData("imageSmall", "", file);
                  clearFieldError("file");
                }}
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoMainTab;
